import { Mutex } from 'async-mutex'
import { fetchBaseQuery } from '@reduxjs/toolkit/query'
import UserPool from '../../pages/auth/UserPool'
import { CognitoUser, CognitoUserSession } from 'amazon-cognito-identity-js'
import { toast } from 'react-toastify'
import 'react-toastify/dist/ReactToastify.css'

const baseUrl = process.env.REACT_APP_API_BASEURL

const getSession = (cognitoUser: CognitoUser): Promise<CognitoUserSession | null> => {
    return new Promise((resolve, reject) => {
        cognitoUser.getSession((error: null | Error, session: null | CognitoUserSession) => {
            if (error) {
                console.log(error)
                if (error.name === 'NotAuthorizedException' || error.name === 'UserNotFoundException') {
                    cognitoUser.signOut()
                    window.location.href = '/auth/sign-in'
                }
                reject(error)
            }
            resolve(session)
        })
    })
}

// Create a new mutex
const mutex = new Mutex()

const baseQuery = fetchBaseQuery({
    baseUrl,
    prepareHeaders: async (headers) => {
        const cognitoUser = UserPool.getCurrentUser()
        if (cognitoUser) {
            const session = await getSession(cognitoUser)
            if (session) {
                headers.set('Authorization', `Bearer ${session.getIdToken().getJwtToken()}`)
            }
        }
        return headers
    }
})

const customFetchBase = async (args: any, api: any, extraOptions: any) => {
    await mutex.waitForUnlock()

    // User validation before any secured API call
    const cognitoUser = UserPool.getCurrentUser()
    let token = ''
    if (cognitoUser) {
        const session = await getSession(cognitoUser)
        if (session) {
            token = session.getIdToken().getJwtToken()
        }
    }

    if (token) {
        try {
            const validationResponse = await fetch(`${baseUrl}/userValidation`, {
                method: 'GET',
                headers: {
                    Authorization: `Bearer ${token}`
                }
            })
            if (validationResponse.status === 401) {
                cognitoUser?.signOut()
                window.location.href = '/auth/sign-in'
                return { error: { status: 401, data: 'Unauthorized' } }
            }
        } catch (err) {
            toast.error('User validation failed. Please try again.')
            cognitoUser?.signOut()
            localStorage.clear()
            window.location.href = '/auth/sign-in'
            return { error: { status: 401, data: 'Unauthorized' } }
        }
    }

    let result = await baseQuery(args, api, extraOptions)

    if (result.error) {
        if (result.error.status === 'FETCH_ERROR') {
            toast.error(
                `We apologize, but it appears that something unexpected has occurred. Please try again, and if the issue persists, kindly contact the administrator for assistance.`
            )
        }
    }
    return result
}

export default customFetchBase
